import { useState } from "react";

// ─── BADGE ──────────────────────────────────────────────
const badgeStyles = {
  ok:    { bg: "#e8f5e0", color: "#2d6a0a" },
  warn:  { bg: "#fff3e0", color: "#8a4c00" },
  late:  { bg: "#fdecea", color: "#a32d2d" },
  open:  { bg: "#fdecea", color: "#a32d2d" },
  prog:  { bg: "#fff3e0", color: "#8a4c00" },
  done:  { bg: "#e8f5e0", color: "#2d6a0a" },
  info:  { bg: "#e3f1fb", color: "#1a3a5c" },
  pend:  { bg: "#fff3e0", color: "#8a4c00" },
};

export function Badge({ type = "info", children, style = {} }) {
  const s = badgeStyles[type] || badgeStyles.info;
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 9px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: s.bg,
      color: s.color,
      whiteSpace: "nowrap",
      ...style
    }}>{children}</span>
  );
}

// ─── BUTTON ─────────────────────────────────────────────
export function Btn({ variant = "outline", children, onClick, disabled, style = {}, small }) {
  const base = {
    display: "flex", alignItems: "center", gap: 7,
    padding: small ? "6px 12px" : "10px 16px",
    borderRadius: 10,
    fontSize: small ? 12 : 13,
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    border: "none",
    fontFamily: "inherit",
    transition: "opacity 0.15s",
    width: "100%",
    justifyContent: "center",
    ...style
  };
  const variants = {
    primary: { background: "#1a3a5c", color: "white" },
    secondary: { background: "#2e86c1", color: "white" },
    outline: { background: "transparent", color: "#1a3a5c", border: "1.5px solid #1a3a5c" },
    ghost: { background: "#f0f4f8", color: "#1a3a5c" },
    danger: { background: "#fdecea", color: "#a32d2d", border: "1.5px solid #f5b8b8" },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// ─── CARD ───────────────────────────────────────────────
export function Card({ children, onClick, style = {}, accent }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "white",
        border: accent ? `2px solid ${accent}` : "0.5px solid #d0dce8",
        borderRadius: 14,
        padding: "12px 14px",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s",
        ...style
      }}
    >{children}</div>
  );
}

// ─── AVATAR ─────────────────────────────────────────────
const avatarColors = [
  ["#e3f1fb","#1a3a5c"],["#e8f5e0","#2d6a0a"],["#fdecea","#a32d2d"],
  ["#fff3e0","#8a4c00"],["#f3e8fb","#5c1a8c"],["#e0f7f4","#0a5c52"],
];
export function Avatar({ name = "?", size = 36, index = 0 }) {
  const [bg, fg] = avatarColors[index % avatarColors.length];
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: bg, color: fg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.33, fontWeight: 700, flexShrink: 0
    }}>{initials}</div>
  );
}

// ─── SECTION TITLE ──────────────────────────────────────
export function SectionTitle({ children }) {
  return (
    <p style={{
      fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em",
      color: "#7a90a8", padding: "12px 16px 4px", fontWeight: 600
    }}>{children}</p>
  );
}

// ─── TICKET STATUS BADGE ────────────────────────────────
export function TicketStatusBadge({ status }) {
  const map = {
    open:  { type: "open",  label: "Ouvert" },
    prog:  { type: "prog",  label: "En cours" },
    done:  { type: "done",  label: "Résolu" },
  };
  const s = map[status] || map.open;
  return <Badge type={s.type}>{s.label}</Badge>;
}

// ─── PAYMENT STATUS BADGE ───────────────────────────────
export function PaymentStatusBadge({ status }) {
  const map = {
    paid:     { type: "ok",   label: "Payé" },
    pending:  { type: "pend", label: "À confirmer" },
    late:     { type: "late", label: "En retard" },
    due_soon: { type: "warn", label: "Bientôt dû" },
  };
  const s = map[status] || map.late;
  return <Badge type={s.type}>{s.label}</Badge>;
}

// ─── EMPTY STATE ────────────────────────────────────────
export function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 20px", color: "#7a90a8" }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
      <p style={{ fontWeight: 600, color: "#2a4a6a", marginBottom: 4 }}>{title}</p>
      <p style={{ fontSize: 13 }}>{subtitle}</p>
    </div>
  );
}

// ─── LOADING SPINNER ────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
      <div style={{
        width: 28, height: 28, border: "3px solid #d0dce8",
        borderTopColor: "#2e86c1", borderRadius: "50%",
        animation: "spin 0.7s linear infinite"
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── MODAL ──────────────────────────────────────────────
export function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(10,20,40,0.55)",
      display: "flex", alignItems: "flex-end", justifyContent: "center"
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "white", borderRadius: "20px 20px 0 0",
          padding: "20px 16px 32px", width: "100%", maxWidth: 480,
          animation: "slideUp 0.25s ease"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ fontWeight: 700, fontSize: 16, color: "#1a3a5c" }}>{title}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, color: "#7a90a8", cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─── INPUT ──────────────────────────────────────────────
export function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ fontSize: 12, color: "#4a6a8a", display: "block", marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      <input
        style={{
          width: "100%", border: "1.5px solid #d0dce8", borderRadius: 10,
          padding: "10px 12px", fontSize: 14, outline: "none", fontFamily: "inherit",
          background: "#f7fafd", color: "#1a2a3a", boxSizing: "border-box"
        }}
        {...props}
      />
    </div>
  );
}

// ─── TEXTAREA ───────────────────────────────────────────
export function Textarea({ label, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ fontSize: 12, color: "#4a6a8a", display: "block", marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      <textarea
        style={{
          width: "100%", border: "1.5px solid #d0dce8", borderRadius: 10,
          padding: "10px 12px", fontSize: 14, outline: "none", fontFamily: "inherit",
          background: "#f7fafd", color: "#1a2a3a", resize: "none",
          boxSizing: "border-box", minHeight: 80
        }}
        {...props}
      />
    </div>
  );
}

// ─── CATEGORY PILLS ─────────────────────────────────────
export function CategoryPills({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
            border: value === opt ? "1.5px solid #2e86c1" : "1.5px solid #d0dce8",
            background: value === opt ? "#e3f1fb" : "white",
            color: value === opt ? "#1a3a5c" : "#5a7a9a",
            cursor: "pointer", fontFamily: "inherit"
          }}
        >{opt}</button>
      ))}
    </div>
  );
}
